// frontend/src/components/landing/sections/TwoLenses.js
import React from 'react';
import { Box, Typography } from '@mui/material';
import { Container, Reveal, Section, TiltCard, staggerItem } from '../parts';
import useReveal from '../useReveal';
import { ArrowRightIcon, ArrowUpIcon } from '../icons';
import { tokens } from '../landingTheme';

// The utterance-type cycle: teacher opens → students respond → teacher follows up
const UT_COLUMNS = [
    { title: 'Teacher opens', items: ['Question', 'Prompt', 'Explanation'] },
    { title: 'Students respond', items: ['Confirms', 'With a statement', 'With a question'], accent: true },
    { title: 'Teacher follows up', items: ['Confirms', 'With a statement', 'With a question'] },
];

// The reasoning-component hierarchy, deepest first (5-class RC without O)
const RC_TIERS = [
    { title: 'Scientific · inferential', items: ['Inference from data'], tone: 'deep' },
    { title: 'Scientific · descriptive', items: ['Scientific knowledge', 'Observation / data'], tone: 'mid' },
    { title: 'Everyday', items: ['Everyday reasoning'], tone: 'light' },
];

const TIER_TONES = {
    deep: { bgcolor: tokens.accentDeep, color: '#FFFFFF' },
    mid: { bgcolor: tokens.accentMid, color: '#FFFFFF' },
    light: { bgcolor: tokens.accentTint, color: tokens.accent },
};

const Pill = ({ children, accent, sx }) => (
    <Box
        component="li"
        sx={{
            ...sx,
            px: 1.5,
            py: 0.75,
            borderRadius: 999,
            fontSize: '0.8125rem',
            fontWeight: 500,
            textAlign: 'center',
            ...(accent
                ? { bgcolor: tokens.accentTint, color: tokens.accent }
                : { bgcolor: tokens.surface, color: tokens.ink, boxShadow: `inset 0 0 0 1px ${tokens.line}` }),
        }}
    >
        {children}
    </Box>
);

const DiagramLabel = ({ children, sx }) => (
    <Typography variant="overline" component="p" sx={{ color: tokens.inkMuted, display: 'block', fontSize: '0.6875rem', ...sx }}>
        {children}
    </Typography>
);

const UtteranceTypeDiagram = () => {
    const [ref, shown] = useReveal();
    return (
        <Box component="figure" ref={ref} sx={{ m: 0, mt: 4 }}>
            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: '1fr 24px 1fr 24px 1fr' },
                    alignItems: 'start',
                    gap: { xs: 1.5, sm: 1 },
                }}
            >
                {UT_COLUMNS.map((column, i) => (
                    <React.Fragment key={column.title}>
                        {i > 0 && (
                            <Box
                                aria-hidden="true"
                                sx={{
                                    display: 'grid',
                                    placeItems: 'center',
                                    color: tokens.inkMuted,
                                    pt: { sm: 5 },
                                    transform: { xs: 'rotate(90deg)', sm: 'none' },
                                }}
                            >
                                <ArrowRightIcon size={18} />
                            </Box>
                        )}
                        <Box>
                            <DiagramLabel sx={{ textAlign: 'center', mb: 1 }}>{column.title}</DiagramLabel>
                            <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0, display: 'grid', gap: 1 }}>
                                {column.items.map((item, row) => (
                                    <Pill key={item} accent={column.accent} sx={staggerItem(shown, 200 + i * 220 + row * 70)}>
                                        {item}
                                    </Pill>
                                ))}
                            </Box>
                        </Box>
                    </React.Fragment>
                ))}
            </Box>
            {/* Loop from "teacher follows up" back to "students respond" */}
            <Box
                aria-hidden="true"
                sx={{
                    display: { xs: 'none', sm: 'block' },
                    position: 'relative',
                    ml: 'calc(50% - 4px)',
                    width: 'calc(33.3% + 8px)',
                    height: 20,
                    mt: 0.5,
                    border: `1.5px dashed ${tokens.accentMid}`,
                    borderTop: 'none',
                    borderRadius: '0 0 10px 10px',
                    '&::before': {
                        content: '""',
                        position: 'absolute',
                        left: -5.5,
                        top: -4,
                        borderLeft: '4.5px solid transparent',
                        borderRight: '4.5px solid transparent',
                        borderBottom: `6px solid ${tokens.accentMid}`,
                    },
                }}
            />
            <Typography
                component="figcaption"
                variant="body2"
                sx={{ mt: 1.5, textAlign: { sm: 'center' }, color: tokens.accentMid, fontStyle: 'italic' }}
            >
                A follow-up question invites another response
            </Typography>
        </Box>
    );
};

const ReasoningDiagram = () => {
    const [ref, shown] = useReveal();
    return (
        <Box component="figure" ref={ref} sx={{ m: 0, mt: 4, display: 'grid', gridTemplateColumns: '40px 1fr', gap: 2 }}>
            <Box aria-hidden="true" sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', color: tokens.inkMuted }}>
                <ArrowUpIcon size={18} />
                <Box sx={{ flex: 1, width: '1.5px', bgcolor: tokens.line, my: 0.5 }} />
                <DiagramLabel sx={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)', lineHeight: 1 }}>Deeper</DiagramLabel>
            </Box>
            <Box component="ol" aria-label="Reasoning components, deepest first" sx={{ listStyle: 'none', m: 0, p: 0, display: 'grid', gap: 2 }}>
                {RC_TIERS.map((tier, i) => (
                    // Everyday (last) arrives first, so the tiers stack up
                    <Box component="li" key={tier.title} sx={staggerItem(shown, 200 + (RC_TIERS.length - 1 - i) * 220, 'translateY(20px)')}>
                        <DiagramLabel sx={{ mb: 1 }}>{tier.title}</DiagramLabel>
                        <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0, display: 'grid', gridTemplateColumns: `repeat(${tier.items.length}, 1fr)`, gap: 1 }}>
                            {tier.items.map((item) => (
                                <Box
                                    component="li"
                                    key={item}
                                    sx={{ px: 1.5, py: 1.25, borderRadius: 3, fontSize: '0.875rem', fontWeight: 500, textAlign: 'center', ...TIER_TONES[tier.tone] }}
                                >
                                    {item}
                                </Box>
                            ))}
                        </Box>
                    </Box>
                ))}
            </Box>
        </Box>
    );
};

// Bento: a wide tile for the utterance-type cycle beside a narrower one for the reasoning hierarchy
const TwoLenses = () => (
    <Section id="what-it-measures" aria-labelledby="what-it-measures-title" sx={{ bgcolor: tokens.subtle }}>
        <Container>
            <Reveal>
                <Typography id="what-it-measures-title" variant="h2" component="h2">
                    Two lenses on every utterance
                </Typography>
            </Reveal>
            <Box
                sx={{
                    mt: { xs: 6, md: 8 },
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', md: '7fr 5fr' },
                    gap: 3,
                }}
            >
                <Reveal delay={0}>
                    <TiltCard sx={{ p: { xs: 3, md: 5 }, height: '100%' }}>
                        <Typography variant="h3" component="h3">Utterance type</Typography>
                        <Typography variant="body1" sx={{ mt: 1.5, color: tokens.inkMuted, maxWidth: 480 }}>
                            What each turn does, and how turns connect: the teacher opens, students respond, and the
                            teacher&rsquo;s follow-up can invite another response.
                        </Typography>
                        <UtteranceTypeDiagram />
                    </TiltCard>
                </Reveal>
                <Reveal delay={120}>
                    <TiltCard sx={{ p: { xs: 3, md: 5 }, height: '100%' }}>
                        <Typography variant="h3" component="h3">Reasoning component</Typography>
                        <Typography variant="body1" sx={{ mt: 1.5, color: tokens.inkMuted }}>
                            The kind of reasoning in each utterance, from everyday to scientific, and within science from
                            describing to inferring.
                        </Typography>
                        <ReasoningDiagram />
                    </TiltCard>
                </Reveal>
            </Box>
        </Container>
    </Section>
);

export default TwoLenses;
